// components/RoleManagement.jsx
import React, { useState, useEffect } from "react";
import { Route, Routes, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import {
    Shield, Users, Eye, Plus, Edit, Trash2, Search, Filter,
    TrendingUp, UserCheck, Settings, Save, X, ChevronDown, Check,
    MessageCircle
} from "lucide-react";

import profilePic from "../../images/profilepic.png";
import { useSelector } from "react-redux";

const RoleManagement = () => {
    const location = useLocation();
    const [stats, setStats] = useState({
        totalUsers: 0,
        usersWithRoles: 0,
        usersWithoutRoles: 0,
        fullAdmins: 0
    });
    const portalInfo = useSelector((state) => state?.portalInfo?.data);
    const portalType = portalInfo?.portalType || 'Community';
    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("with-roles");
    const [showFilters, setShowFilters] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [filters, setFilters] = useState({
        type: "",
        department: "",
        hasRoles: ""
    });

    useEffect(() => {
        fetchStats();
        fetchUsers();
        fetchAllUsers();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/roles/stats/overview`);
            setStats(response.data.data);
        } catch (error) {
            console.error("Error fetching stats:", error);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/roles`);
            setUsers(response.data.data.users);
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUsers = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/roles/all-users`);
            setAllUsers(response.data.data.users);
        } catch (error) {
            console.error("Error fetching all users:", error);
        }
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedUser(null);
        // Refresh data after modal closes
        fetchUsers();
        fetchAllUsers();
        fetchStats();
    };

    // Filter users based on search and filters
    const filterUsers = (userList) => {
        return userList.filter(user => {
            const matchesSearch = 
                user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesType = !filters.type || user.type === filters.type;
            const matchesDepartment = !filters.department || user.department === filters.department;

            return matchesSearch && matchesType && matchesDepartment;
        });
    };

    // Get users with roles
    const usersWithRoles = filterUsers(users.filter(user => {
        if (!user.role) return false;
        const roleString = Object.keys(user.role)
            .filter(module =>
                typeof user.role[module] === "object" &&
                ['all', 'view', 'create', 'update', 'delete'].some(
                    perm => user.role[module][perm]
                )
            );
        return roleString.length > 0;
    }));

    // Get all users for second tab
    const filteredAllUsers = filterUsers(allUsers);

    const Stat = ({ title, value, icon, tint }) => (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-600 font-medium">{title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${tint}`}>
                    {icon}
                </div>
            </div>
        </div>
    );

    const clearFilters = () => {
        setFilters({ type: "", department: "", hasRoles: "" });
        setSearchTerm("");
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="w-full max-w-7xl mx-auto px-4 lg:px-6 py-6">
                {/* Header */}
                <header className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl dynamic-site-bg flex items-center justify-center shadow-lg">
                            <Shield size={24} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Role Management</h1>
                            <p className="text-gray-600 mt-1">Manage user permissions and access levels</p>
                        </div>
                    </div>
                </header>

                {/* Stats */}
                <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <Stat
                        title="Total Users"
                        value={stats.totalUsers}
                        icon={<Users size={24} className="text-blue-600" />}
                        tint="bg-blue-50"
                    />
                    <Stat
                        title="With Roles"
                        value={stats.usersWithRoles}
                        icon={<UserCheck size={24} className="text-emerald-600" />}
                        tint="bg-emerald-50"
                    />
                    <Stat
                        title="Without Roles"
                        value={stats.usersWithoutRoles}
                        icon={<Settings size={24} className="text-amber-600" />}
                        tint="bg-amber-50"
                    />
                    <Stat
                        title="Full Admins"
                        value={stats.fullAdmins}
                        icon={<Shield size={24} className="text-red-600" />}
                        tint="bg-red-50"
                    />
                </section>

                {/* Search and Filters */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                    <div className="flex flex-col lg:flex-row gap-4">
                        {/* Search Bar */}
                        <div className="flex-1 relative">
                            <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-10 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>

                        {/* Filter Toggle */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center gap-2 px-6 py-3 border rounded-lg font-medium transition-colors ${
                                showFilters 
                                ? 'bg-blue-50 border-blue-200 text-blue-700' 
                                : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <Filter size={18} />
                            Filters
                            <ChevronDown size={18} className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                        </button>
                    </div>

                    {/* Filter Options */}
                    {showFilters && (
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <select
                                    value={filters.type}
                                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                                    className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">All Types</option>
                                    <option value="admin">Admin</option>
                                    <option value="alumni">Alumni</option>
                                    <option value="student">Student</option>
                                </select>

                                <input
                                    type="text"
                                    placeholder="Filter by department..."
                                    value={filters.department}
                                    onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                                    className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />

                                <button
                                    onClick={clearFilters}
                                    className="px-6 py-3 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
                                >
                                    Clear All
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                    <nav className="flex border-b border-gray-100">
                        <button
                            onClick={() => setActiveTab("with-roles")}
                            className={`flex-1 py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === "with-roles"
                                    ? "border-blue-500 text-blue-600 bg-blue-50"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                            Users with Admin Access ({usersWithRoles.length})
                        </button>
                        <button
                            onClick={() => setActiveTab("all-users")}
                            className={`flex-1 py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === "all-users"
                                    ? "border-blue-500 text-blue-600 bg-blue-50"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                            All Users ({filteredAllUsers.length})
                        </button>
                    </nav>

                    <TabContent 
                        activeTab={activeTab} 
                        usersWithRoles={usersWithRoles}
                        allUsers={filteredAllUsers}
                        loading={loading}
                        onEditUser={handleEditUser}
                    />
                </div>
            </div>

            {/* Role Editor Modal */}
            {showModal && selectedUser && (
                <UserRoleModal 
                    user={selectedUser} 
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};

// Tab Content Component
const TabContent = ({ activeTab, usersWithRoles, allUsers, loading, onEditUser }) => {
       const portalInfo = useSelector((state) => state?.portalInfo?.data);
    const portalType = portalInfo?.portalType || 'Community';
    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading users...</p>
                </div>
            </div>
        );
    }

    const currentUsers = activeTab === "with-roles" ? usersWithRoles : allUsers;

    return (
        <div className="p-6">
            <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                    {activeTab === "with-roles" ? "Users with Admin Access" : "All Users"}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                    {activeTab === "with-roles" 
                        ? "Users who currently have admin permissions"
                        : "All users in the system - manage their access levels"
                    }
                </p>
            </div>

            <div className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    User
                                </th>
                         {portalType !=='Community' && <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Type
                                </th>}
                               
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    {activeTab === "with-roles" ? "Admin Modules" : "Access Status"}
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {currentUsers.map((user) => (
                                <UserRow 
                                    key={user._id} 
                                    user={user} 
                                    showRoleDetails={activeTab === "with-roles"}
                                    onEdit={() => onEditUser(user)}
                                />
                            ))}
                            {currentUsers.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-16 text-center">
                                        <div className="text-gray-400">
                                            <Users size={48} className="mx-auto mb-4 opacity-50" />
                                            <p className="text-lg font-medium">No users found</p>
                                            <p className="text-sm">Try adjusting your search or filters</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// User Row Component
const UserRow = ({ user, showRoleDetails, onEdit }) => {
       const portalInfo = useSelector((state) => state?.portalInfo?.data);
    const portalType = portalInfo?.portalType || 'Community';
    const roleString = user.role ? Object.keys(user.role)
        .filter(module =>
            typeof user.role[module] === "object" &&
            ['all', 'view', 'create', 'update', 'delete'].some(
                perm => user.role[module][perm]
            )
        )
        .map(name => name.charAt(0).toUpperCase() + name.slice(1))
        .join(", ") : null;

    const hasAnyRole = roleString && roleString.length > 0;

    return (
        <tr className="hover:bg-gray-50 transition-colors">
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                        <img
                            className="h-10 w-10 rounded-full object-cover border-2 border-gray-100"
                            src={user.profilePicture || profilePic}
                            alt=""
                        />
                    </div>
                    <div className="ml-4">
                        <div className="text-sm font-semibold text-gray-900">
                            {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                </div>
            </td>
        {portalType !=='Community' &&    <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    user.type === 'admin' ? 'bg-red-100 text-red-800' :
                    user.type === 'alumni' ? 'bg-blue-100 text-blue-800' :
                    'bg-emerald-100 text-emerald-800'
                }`}>
                    {user.type?.toUpperCase()}
                </span>
            </td>}
          
            <td className="px-6 py-4 whitespace-nowrap">
                {showRoleDetails ? (
                    <div className="text-sm text-gray-900 truncate max-w-[250px]">
                        {roleString || <span className="text-gray-400">No specific roles</span>}
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs  font-semibold rounded-full ${
                            hasAnyRole 
                                ? 'bg-emerald-100 text-emerald-800' 
                                : 'bg-gray-100 text-gray-800'
                        }`}>
                            {hasAnyRole && <Check size={12} className="mr-1" />}
                            {hasAnyRole ? 'Has Access' : 'No Access'}
                        </span>
                        {hasAnyRole && (
                            <span className="text-xs text-gray-500 truncate max-w-32">
                                ({roleString})
                            </span>
                        )}
                    </div>
                )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                    onClick={onEdit}
                    className="inline-flex items-center gap-1.5 px-4 py-2 dynamic-site-bg text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                >
                    <Edit size={14} />
                    {hasAnyRole ? 'Edit Access' : 'Grant Access'}
                </button>
            </td>
        </tr>
    );
};

// Modal Component for Role Editor
const UserRoleModal = ({ user, onClose }) => {
    const [roles, setRoles] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const modules = [
        { key: 'feeds', label: 'Feeds', icon: <TrendingUp size={16} />, color: 'bg-blue-500' },
        { key: 'members', label: 'Members', icon: <Users size={16} />, color: 'bg-emerald-500' },
        { key: 'groups', label: 'Groups', icon: <Users size={16} />, color: 'bg-purple-500' },
        { key: 'news', label: 'News', icon: <Plus size={16} />, color: 'bg-indigo-500' },
        { key: 'events', label: 'Events', icon: <Plus size={16} />, color: 'bg-pink-500' },
        { key: 'donations', label: 'Donations', icon: <Plus size={16} />, color: 'bg-pink-500' },
        { key: 'messages', label: 'Messages', icon: <MessageCircle size={16} />, color: 'bg-green-500' },
        { key: 'photoGallery', label: 'Gallery', icon: <Plus size={16} />, color: 'bg-amber-500' },
        { key: 'profanity', label: 'Profanity', icon: <Shield size={16} />, color: 'bg-red-500' },
        // { key: 'fullAdmin', label: 'Full Admin', icon: <Shield size={16} />, color: 'bg-gray-500' }
    ];

    const actions = [
        { key: 'all', label: 'All' },
        { key: 'view', label: 'View' },
        { key: 'create', label: 'Create' },
        { key: 'update', label: 'Update' },
        { key: 'delete', label: 'Delete' }
    ];

    useEffect(() => {
        fetchUserRoles();
    }, [user._id]);

    const fetchUserRoles = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/roles/${user._id}`);
            setRoles(response.data.data.role);
        } catch (error) {
            console.error("Error fetching user roles:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionChange = (module, action, value) => {
        setRoles(prev => ({
            ...prev,
            [module]: {
                ...prev[module],
                [action]: value
            }
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await axios.put(`${process.env.REACT_APP_API_URL}/roles/${user._id}`, roles);
            onClose();
        } catch (error) {
            console.error("Error saving roles:", error);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-center mt-4">Loading user roles...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full  overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-4">
                        <img
                            src={user.profilePicture || profilePic}
                            alt=""
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                        />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                {user.firstName} {user.lastName}
                            </h2>
                            <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 max-h-[calc(90vh-140px)] overflow-y-auto">
                    <div className="grid gap-4">
                        {modules.map((module) => (
                            <div key={module.key} className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className={`w-8 h-8 rounded-lg ${module.color} flex items-center justify-center text-white`}>
                                        {module.icon}
                                    </div>
                                    <h3 className="font-semibold text-gray-900">{module.label}</h3>
                                </div>

                                <div className="grid grid-cols-5 gap-3">
                                    {actions.map((action) => (
                                        <label key={action.key} className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={roles[module.key]?.[action.key] || false}
                                                onChange={(e) => handlePermissionChange(module.key, action.key, e.target.checked)}
                                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700">
                                                {action.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-6 py-2 dynamic-site-bg text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 font-medium flex items-center gap-2"
                    >
                        <Save size={16} />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RoleManagement;
